import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Shell from "@/layout/Shell";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import Badge from "@/components/Badge";
import EmptyState from "@/components/EmptyState";
import { fmtDate, cn } from "@/lib/utils";
import { BookOpen, Building2, ChevronRight, Search, Upload, Edit2, X, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function fileToBase64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
}

export default function MyBookings() {
  const { data: raw, loading, reload } = useFetch(() => api.myBookings().then(r => r?.bookings || []), []);
  const [q, setQ]         = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null); // selected booking for modal
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [letterFile, setLetterFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const all = raw || [];
  const FILTERS = [["all","All"],["pending","Pending"],["confirmed","Confirmed"],["cancelled","Cancelled"]];
  const bookings = all
    .filter(b => filter === "all" || b.status === filter)
    .filter(b => !q || [b.eventName, b.auditorium, b.bookingID].join(" ").toLowerCase().includes(q.toLowerCase()));

  const openDetail = async (b) => {
    setDetail(b); setEditing(false);
    setEditForm({ eventName: b.eventName || "", deptClub: b.deptClub || "", specialReqs: b.specialReqs || "" });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.editBooking(detail.bookingID, editForm);
      toast.success("Booking updated");
      setEditing(false); reload();
      setDetail(d => ({ ...d, ...editForm }));
    } catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const cancelBooking = async () => {
    if (!window.confirm(`Cancel booking ${detail.bookingID}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.cancelBooking(detail.bookingID);
      toast.success("Booking cancelled");
      setDetail(null); reload();
    } catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const uploadLetter = async () => {
    if (!letterFile) return;
    setBusy(true);
    try {
      const b64 = await fileToBase64(letterFile);
      const r = await api.uploadLetter(detail.bookingID, letterFile.name, letterFile.type, b64);
      toast.success(r.message || "Letter uploaded");
      setLetterFile(null); reload();
    } catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Shell role="portal" title="My bookings" subtitle="All your hall bookings">
      {/* Search + filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search bookings…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <div className="flex gap-1 bg-muted rounded-xl p-1">
          {FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={cn("px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150",
                filter === v ? "bg-card text-blue-600 shadow-sm" : "text-muted-foreground hover:text-foreground")}
            >{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : bookings.length === 0 ? (
        <EmptyState icon={BookOpen} title="No bookings" description={q ? "No bookings match your search." : "You haven't made any hall bookings yet."} />
      ) : (
        <div className="space-y-2">
          {bookings.map(b => (
            <button key={b.bookingID} onClick={() => openDetail(b)}
              className="w-full surface rounded-2xl p-4 flex items-center gap-3.5 text-left card-hover"
            >
              <div className="w-11 h-11 rounded-xl bg-blue-600/10 border border-blue-600/15 flex items-center justify-center shrink-0">
                <Building2 className="h-4.5 w-4.5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-foreground">{b.eventName || b.bookingID}</div>
                <div className="text-xs text-muted-foreground">{b.auditorium} · {fmtDate(b.date)}{b.timeSlot ? ` · ${b.timeSlot}` : ""}</div>
                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{b.bookingID}</div>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <Badge status={b.status} />
                {!b.hasEvent && b.status === "confirmed" && (
                  <Link to={`/portal/events?bid=${b.bookingID}`} onClick={e => e.stopPropagation()} className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline">+ Host event</Link>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Booking detail modal */}
      <Dialog open={!!detail} onOpenChange={v => !v && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold text-base">Booking detail</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-2 text-sm">
                {[
                  ["Booking ID",   detail.bookingID],
                  ["Hall",         detail.auditorium],
                  ["Date",         fmtDate(detail.date)],
                  ["Time slot",    detail.timeSlot],
                  ["Status",       null],
                  ["Dept / Club",  detail.deptClub],
                  ["Coordinator",  detail.name],
                  ["Phone",        detail.phone],
                  ["Note",         detail.adminNote],
                ].map(([k, v]) => v !== undefined && (
                  <div key={k} className="flex justify-between py-1.5 border-b border-border/30 gap-4">
                    <span className="text-muted-foreground shrink-0">{k}</span>
                    {k === "Status" ? <Badge status={detail.status} /> : <span className="font-medium text-right text-xs break-all">{v || "—"}</span>}
                  </div>
                ))}
              </div>

              {/* Permission letter */}
              {detail.permissionLetter && (
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                  {detail.permissionLetter.startsWith("http")
                    ? <a href={detail.permissionLetter} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:underline truncate">View permission letter</a>
                    : <span className="text-muted-foreground">Not uploaded yet</span>
                  }
                </div>
              )}

              {/* Upload letter */}
              {(detail.status === "pending" || detail.status === "confirmed") && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Upload / replace permission letter</Label>
                  <div className="flex gap-2">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => setLetterFile(e.target.files?.[0] || null)}
                      className="flex-1 text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-blue-600/10 file:text-blue-600 dark:file:text-blue-400 cursor-pointer" />
                    {letterFile && (
                      <Button size="sm" onClick={uploadLetter} disabled={busy} className="gap-1.5 shrink-0">
                        <Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : "Upload"}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Edit form */}
              {editing ? (
                <div className="space-y-3 border-t border-border/40 pt-4">
                  <div className="space-y-1.5"><Label>Event name</Label>
                    <Input value={editForm.eventName} onChange={e => setEditForm(p => ({ ...p, eventName: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5"><Label>Dept / Club</Label>
                    <Input value={editForm.deptClub} onChange={e => setEditForm(p => ({ ...p, deptClub: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5"><Label>Special requirements</Label>
                    <Textarea rows={2} value={editForm.specialReqs} onChange={e => setEditForm(p => ({ ...p, specialReqs: e.target.value }))} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={saveEdit} disabled={busy} className="flex-1">{busy ? "Saving…" : "Save changes"}</Button>
                    <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 pt-2 border-t border-border/40">
                  {(detail.status === "pending" || detail.status === "confirmed") && (
                    <Button variant="outline" onClick={() => setEditing(true)} className="gap-1.5 flex-1">
                      <Edit2 className="h-3.5 w-3.5" />Edit
                    </Button>
                  )}
                  {detail.status !== "cancelled" && (
                    <Button variant="outline" onClick={cancelBooking} disabled={busy} className="gap-1.5 flex-1 text-destructive hover:text-destructive">
                      <X className="h-3.5 w-3.5" />Cancel booking
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
